import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  opshubApi,
  type CommentRecord,
  type OpsHubDashboard,
  type TaskRecord,
  type TaskRecommendation,
  type TeamRecord,
  type WorkloadMetrics,
} from '../../api/opshub';

type Tab = 'hub' | 'kanban' | 'calendar' | 'tasks' | 'teams' | 'personal' | 'workload';

const KANBAN_COLUMNS = [
  { status: 'open', label: 'Open' },
  { status: 'assigned', label: 'Assigned' },
  { status: 'in_progress', label: 'In Progress' },
  { status: 'waiting_approval', label: 'Waiting Approval' },
  { status: 'blocked', label: 'Blocked' },
  { status: 'ready_to_publish', label: 'Ready to Publish' },
  { status: 'completed', label: 'Completed' },
];

function statusClass(s: string) {
  if (s === 'in_progress') return 'text-sky-700 bg-sky-50';
  if (s === 'completed') return 'text-emerald-700 bg-emerald-50';
  if (s === 'blocked') return 'text-red-700 bg-red-50';
  if (s === 'waiting_approval') return 'text-amber-700 bg-amber-50';
  if (s === 'ready_to_publish') return 'text-violet-700 bg-violet-50';
  return 'text-slate-600 bg-slate-50';
}

function domainLabel(d: string) {
  return d.replace(/_/g, ' ');
}

export function OpsHubPage() {
  const [tab, setTab] = useState<Tab>('hub');
  const [dashboard, setDashboard] = useState<OpsHubDashboard | null>(null);
  const [tasks, setTasks] = useState<TaskRecord[]>([]);
  const [teams, setTeams] = useState<TeamRecord[]>([]);
  const [comments, setComments] = useState<CommentRecord[]>([]);
  const [workload, setWorkload] = useState<WorkloadMetrics[]>([]);
  const [recommendations, setRecommendations] = useState<TaskRecommendation[]>([]);
  const [selectedTask, setSelectedTask] = useState<TaskRecord | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, t, tm, c, wl, recs] = await Promise.all([
        opshubApi.dashboard(),
        opshubApi.listTasks(),
        opshubApi.listTeams(),
        opshubApi.listComments(),
        opshubApi.listWorkload(),
        opshubApi.listRecommendations(),
      ]);
      setDashboard(dash);
      setTasks(t);
      setTeams(tm);
      setComments(c);
      setWorkload(wl);
      setRecommendations(recs);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load operations hub data');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const generateRecs = async () => {
    setBusy(true);
    try {
      await opshubApi.generateRecommendations();
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const tasksByStatus = useMemo(() => {
    const map: Record<string, TaskRecord[]> = {};
    for (const col of KANBAN_COLUMNS) {
      map[col.status] = tasks.filter((t) => t.status === col.status);
    }
    return map;
  }, [tasks]);

  const tabs: { id: Tab; label: string }[] = [
    { id: 'hub', label: 'Operations Hub' },
    { id: 'kanban', label: 'Kanban Board' },
    { id: 'calendar', label: 'Calendar' },
    { id: 'tasks', label: 'Task Workspace' },
    { id: 'teams', label: 'Team Dashboard' },
    { id: 'personal', label: 'Personal Dashboard' },
    { id: 'workload', label: 'Workload Planner' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Operations Hub</h1>
          <p className="text-sm text-slate-500">
            Task intelligence, team collaboration &amp; work management — FRP-00004
          </p>
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={busy}
          className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-50"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium ${
              tab === t.id ? 'bg-sky-100 text-sky-700' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'hub' && dashboard && (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Open Tasks', value: dashboard.open_tasks.toString() },
              { label: 'Completed', value: dashboard.completed_tasks.toString() },
              { label: 'Blocked', value: dashboard.blocked_tasks.toString() },
              { label: 'Overdue', value: dashboard.overdue_tasks.toString() },
              { label: 'Waiting Approval', value: dashboard.waiting_approval.toString() },
              { label: 'Active Teams', value: dashboard.active_teams.toString() },
              { label: 'Execution Velocity', value: dashboard.avg_execution_velocity.toFixed(1) },
              { label: 'Operational SLA', value: `${dashboard.operational_sla_pct.toFixed(0)}%` },
            ].map((kpi) => (
              <div key={kpi.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{kpi.label}</p>
                <p className="mt-1 text-2xl font-semibold text-slate-900">{kpi.value}</p>
              </div>
            ))}
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-100 px-4 py-3">
                <h2 className="font-medium text-slate-900">High Priority Tasks</h2>
              </div>
              <div className="divide-y divide-slate-100">
                {tasks.slice(0, 5).map((t) => (
                  <div key={t.id} className="px-4 py-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-slate-900">{t.title}</span>
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(t.status)}`}>
                        {t.status.replace(/_/g, ' ')}
                      </span>
                      {t.is_overdue && (
                        <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs text-red-700">overdue</span>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-slate-500">
                      {domainLabel(t.domain)} · {t.assignee_name ?? 'Unassigned'}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
              <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
                <h2 className="font-medium text-slate-900">AI Task Copilot</h2>
                <button
                  type="button"
                  onClick={generateRecs}
                  disabled={busy}
                  className="text-xs font-medium text-violet-600 hover:text-violet-800 disabled:opacity-50"
                >
                  Generate
                </button>
              </div>
              <div className="divide-y divide-slate-100">
                {recommendations.slice(0, 4).map((r) => (
                  <div key={r.id} className="px-4 py-3">
                    <p className="font-medium text-slate-900">{r.title}</p>
                    <p className="mt-1 text-xs text-slate-500">
                      {r.recommended_owner && `→ ${r.recommended_owner} · `}
                      Confidence {(r.confidence_score * 100).toFixed(0)}%
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === 'kanban' && (
        <div className="overflow-x-auto pb-4">
          <div className="flex min-w-max gap-4">
            {KANBAN_COLUMNS.map((col) => (
              <div key={col.status} className="w-64 shrink-0 rounded-xl border border-slate-200 bg-slate-50">
                <div className="border-b border-slate-200 px-3 py-2">
                  <h3 className="text-sm font-medium text-slate-700">
                    {col.label} ({tasksByStatus[col.status]?.length ?? 0})
                  </h3>
                </div>
                <div className="space-y-2 p-2">
                  {(tasksByStatus[col.status] ?? []).map((t) => (
                    <div
                      key={t.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => { setSelectedTask(t); setTab('tasks'); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { setSelectedTask(t); setTab('tasks'); } }}
                      className="cursor-pointer rounded-lg border border-slate-200 bg-white p-3 shadow-sm hover:border-sky-300"
                    >
                      <p className="text-sm font-medium text-slate-900">{t.title}</p>
                      <p className="mt-1 text-xs text-slate-500">{domainLabel(t.domain)}</p>
                      {t.assignee_name && (
                        <p className="mt-1 text-xs text-slate-400">{t.assignee_name}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'calendar' && (
        <div className="space-y-3">
          {tasks
            .filter((t) => t.due_date)
            .sort((a, b) => new Date(a.due_date!).getTime() - new Date(b.due_date!).getTime())
            .map((t) => (
              <div key={t.id} className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="w-24 shrink-0 text-center">
                  <p className="text-lg font-semibold text-slate-900">
                    {new Date(t.due_date!).getDate()}
                  </p>
                  <p className="text-xs text-slate-500">
                    {new Date(t.due_date!).toLocaleDateString(undefined, { month: 'short' })}
                  </p>
                </div>
                <div className="flex-1">
                  <p className="font-medium text-slate-900">{t.title}</p>
                  <p className="text-sm text-slate-500">{t.assignee_name ?? 'Unassigned'}</p>
                </div>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(t.status)}`}>
                  {t.status.replace(/_/g, ' ')}
                </span>
              </div>
            ))}
        </div>
      )}

      {tab === 'tasks' && (
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-1 space-y-2">
            {tasks.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setSelectedTask(t)}
                className={`w-full rounded-lg border p-3 text-left text-sm ${
                  selectedTask?.id === t.id ? 'border-sky-300 bg-sky-50' : 'border-slate-200 bg-white hover:bg-slate-50'
                }`}
              >
                <p className="font-medium text-slate-900">{t.title}</p>
                <p className="text-xs text-slate-500">{domainLabel(t.domain)}</p>
              </button>
            ))}
          </div>
          {selectedTask && (
            <div className="lg:col-span-2 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-lg font-medium text-slate-900">{selectedTask.title}</h2>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(selectedTask.status)}`}>
                  {selectedTask.status.replace(/_/g, ' ')}
                </span>
              </div>
              {selectedTask.description && (
                <p className="mt-2 text-sm text-slate-600">{selectedTask.description}</p>
              )}
              <div className="mt-4 grid gap-3 sm:grid-cols-2 text-sm">
                <div><span className="text-slate-500">Domain:</span> {domainLabel(selectedTask.domain)}</div>
                <div><span className="text-slate-500">Assignee:</span> {selectedTask.assignee_name ?? '—'}</div>
                <div><span className="text-slate-500">Source:</span> {selectedTask.source.replace(/_/g, ' ')}</div>
                <div><span className="text-slate-500">Est. hours:</span> {selectedTask.estimated_hours}</div>
                {selectedTask.checklist_total > 0 && (
                  <div>
                    <span className="text-slate-500">Checklist:</span>{' '}
                    {selectedTask.checklist_done}/{selectedTask.checklist_total}
                  </div>
                )}
              </div>
              <div className="mt-6">
                <h3 className="text-sm font-medium text-slate-900">Activity &amp; Comments</h3>
                <div className="mt-2 space-y-2">
                  {comments.filter((c) => c.task_id === selectedTask.id).map((c) => (
                    <div key={c.id} className="rounded bg-slate-50 px-3 py-2 text-sm">
                      <span className="font-medium text-slate-700">{c.author}:</span>{' '}
                      <span className="text-slate-600">{c.content}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'teams' && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {teams.map((team) => (
            <div key={team.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="font-medium text-slate-900">{team.name}</h3>
              <p className="text-sm text-slate-500">{team.department}</p>
              <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-slate-500">Members:</span> {team.member_count}</div>
                <div><span className="text-slate-500">Open tasks:</span> {team.open_tasks}</div>
                <div><span className="text-slate-500">Capacity:</span> {team.capacity_pct}%</div>
              </div>
              {team.members && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {team.members.map((m) => (
                    <span key={m} className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{m}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'personal' && (
        <div className="space-y-3">
          {tasks.filter((t) => t.assignee_name).slice(0, 8).map((t) => (
            <div key={t.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div>
                <p className="font-medium text-slate-900">{t.title}</p>
                <p className="text-sm text-slate-500">{t.assignee_name} · {domainLabel(t.domain)}</p>
              </div>
              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(t.status)}`}>
                {t.status.replace(/_/g, ' ')}
              </span>
            </div>
          ))}
        </div>
      )}

      {tab === 'workload' && (
        <div className="space-y-4">
          {workload.map((w) => (
            <div key={w.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="font-medium text-slate-900">{w.team_name}</h3>
              <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4 text-sm">
                <div><span className="text-slate-500">Open:</span> {w.open_tasks}</div>
                <div><span className="text-slate-500">Completed:</span> {w.completed_tasks}</div>
                <div><span className="text-slate-500">Blocked:</span> {w.blocked_tasks}</div>
                <div><span className="text-slate-500">Overdue:</span> {w.overdue_tasks}</div>
                <div><span className="text-slate-500">Capacity:</span> {w.capacity_pct}%</div>
                <div><span className="text-slate-500">Utilization:</span> {w.utilization_pct}%</div>
                <div><span className="text-slate-500">Velocity:</span> {w.execution_velocity}/wk</div>
                <div><span className="text-slate-500">SLA:</span> {w.operational_sla_pct}%</div>
              </div>
              <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100">
                <div
                  className={`h-full rounded-full ${w.utilization_pct > 85 ? 'bg-red-500' : w.utilization_pct > 70 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                  style={{ width: `${Math.min(w.utilization_pct, 100)}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
